import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatCurrency } from '@/services/order-service';
import { OrderItemResponse } from '@/types/order-type';

interface OrderItemEditDialogProps {
    item: OrderItemResponse | null;
    isOpen: boolean;
    onClose: () => void;
    onSave: (itemId: string, qty: number, price: number) => void;
}

export function OrderItemEditDialog({ item, isOpen, onClose, onSave }: OrderItemEditDialogProps) {
    const [qty, setQty] = useState<string>('');
    const [price, setPrice] = useState<string>('');

    // Sync state when item changes
    useEffect(() => {
        if (item) {
            setQty(item.quantity.toString());
            setPrice(item.price.toString());
        }
    }, [item]);

    if (!item) return null;

    const currentQty = parseInt(qty) || 0;
    const currentPrice = parseFloat(price) || 0;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-[400px] rounded-t-2xl sm:rounded-xl">
                <DialogHeader>
                    <DialogTitle className="text-lg">Edit Order Item</DialogTitle>
                    <p className="inline-block px-2 py-0.5 rounded bg-slate-100 text-slate-900 font-mono text-sm font-bold border border-slate-200">
                        {item.sku}
                    </p>
                </DialogHeader>

                <div className="grid gap-6 py-4">
                    {/* Quantity Control */}
                    <div className="grid gap-2">
                        <Label className="text-xs font-semibold uppercase text-slate-500">Quantity</Label>
                        <div className="flex items-center gap-3">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => setQty(Math.max(1, currentQty - 1).toString())}
                                className="h-12 w-12 rounded-lg text-xl"
                            >-</Button>
                            <Input
                                type="number"
                                value={qty}
                                onChange={(e) => setQty(e.target.value)}
                                className="h-12 text-center font-bold text-lg"
                            />
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => setQty((currentQty + 1).toString())}
                                className="h-12 w-12 rounded-lg text-xl"
                            >+</Button>
                        </div>
                    </div>

                    {/* Price Control */}
                    <div className="grid gap-2">
                        <Label className="text-xs font-semibold uppercase text-slate-500">Unit Price</Label>
                        <div className="relative">
                            <Input
                                type="number"
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                                className="h-12 pl-4 font-mono text-lg"
                            />
                        </div>
                    </div>

                    <div className="p-4 bg-blue-50/50 border border-blue-100 rounded-xl flex justify-between items-center">
                        <span className="text-xs font-medium text-blue-600">Line Total:</span>
                        <span className="text-xl font-bold text-blue-700 font-mono">
                            {formatCurrency(String(currentQty * currentPrice))}
                        </span>
                    </div>
                </div>

                <DialogFooter className="flex-row gap-2">
                    <Button variant="ghost" onClick={onClose} className="flex-1">Cancel</Button>
                    <Button
                        onClick={() => onSave(item.id, currentQty, currentPrice)}
                        className="flex-1"
                    >
                        Confirm Changes
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}